import { useMemo, useState } from 'react';
import { Check, X } from 'lucide-react';
import Button from '../shared/Button';
import { Toast } from '../shared/Toast';
import { SubscriptionService, PLANS, getPriceId } from '../../services/SubscriptionService';
import { useSubscription } from '../../hooks/useSubscription';

const BENEFITS = [
	'Unlimited documents (Laureate) or a fresh document allowance',
	'More source capacity',
	'Advanced document types',
	'Continue working immediately'
];

function intervalToFrontend(backendInterval) {
	const map = { month: 'monthly', '4months': 'quarterly', year: 'yearly' };
	return map[backendInterval] || 'monthly';
}

function buildReturnUrls() {
	const base = window.location.href.split('#')[0].split('?')[0];
	const successUrl = `${base}?wf_checkout=1&session_id={CHECKOUT_SESSION_ID}`;
	const cancelUrl = `${base}?wf_checkout=cancel`;
	return { successUrl, cancelUrl };
}

/**
 * In-context renew / upgrade modal when document or source limits are hit.
 */
export default function LimitReachedModal({ isOpen, onClose, limitPayload, onSuccess }) {
	const {
		getEffectivePlanName,
		getBillingInterval,
		refreshSubscription
	} = useSubscription();
	const [busy, setBusy] = useState(false);
	const [showUpgradePicker, setShowUpgradePicker] = useState(false);

	const planName = limitPayload?.planName || getEffectivePlanName() || 'essentials';
	const planDisplay =
		limitPayload?.planDisplayName ||
		PLANS[planName]?.name ||
		planName;
	const canRenewEarly = !!limitPayload?.canRenewEarly;
	const canUpgrade = limitPayload?.canUpgrade !== false && planName !== 'laureate';
	const isDocument = limitPayload?.limitKind !== 'source';

	const headline = isDocument
		? "You've reached the maximum number of documents included with your current plan."
		: "You've reached the maximum number of sources included with your current plan.";

	const upgradeTargets = useMemo(() => {
		const rank = { essentials: 0, essayist: 1, laureate: 2 };
		const current = rank[planName] ?? 0;
		return Object.entries(PLANS)
			.filter(([key]) => (rank[key] ?? 0) > current)
			.map(([key, plan]) => ({ key, ...plan }));
	}, [planName]);

	if (!isOpen) return null;

	const startCheckout = async (priceId, options = {}) => {
		const { successUrl, cancelUrl } = buildReturnUrls();
		try {
			const response = await SubscriptionService.createCheckoutSession(
				priceId,
				successUrl,
				cancelUrl,
				options
			);
			if (response.status === 200 && response.url) {
				window.location.href = response.url;
				return;
			}
			throw new Error(response.message || 'Failed to start checkout');
		} catch (err) {
			const data = err?.response?.data;
			if (data?.code === 'ALREADY_ON_PLAN' || data?.code === 'ALREADY_ON_HIGHER_PLAN') {
				await refreshSubscription();
				Toast.success(data.message || 'Your subscription is already active. Account refreshed.');
				onClose();
				return;
			}
			throw err;
		}
	};

	const handleRenew = async () => {
		try {
			setBusy(true);
			const result = await SubscriptionService.renewEarly();
			if (result.status === 200) {
				Toast.success(
					result.message ||
						'Renewal successful. Your document and source allowances have been reset for the new billing period.'
				);
				await refreshSubscription();
				onSuccess?.(result);
				onClose();
				return;
			}
			if (result.requiresCheckout || result.status === 402) {
				const priceId =
					result.priceId ||
					getPriceId(planName, intervalToFrontend(getBillingInterval()));
				if (!priceId) {
					throw new Error('Could not resolve a renewal price. Open Subscription to renew.');
				}
				await startCheckout(priceId, { allowEarlyRenewalCheckout: true });
				return;
			}
			throw new Error(result.message || 'Early renewal failed');
		} catch (err) {
			const data = err?.response?.data;
			if (data?.requiresCheckout || err?.response?.status === 402) {
				try {
					const priceId =
						data?.priceId ||
						getPriceId(planName, intervalToFrontend(getBillingInterval()));
					if (priceId) {
						await startCheckout(priceId, { allowEarlyRenewalCheckout: true });
						return;
					}
				} catch (checkoutErr) {
					Toast.error(
						checkoutErr?.response?.data?.message ||
							checkoutErr.message ||
							'Failed to start renewal checkout'
					);
					return;
				}
			}
			Toast.error(data?.message || err.message || 'Failed to renew subscription');
		} finally {
			setBusy(false);
		}
	};

	const handleUpgradePlan = async (targetKey) => {
		try {
			setBusy(true);
			const interval = intervalToFrontend(getBillingInterval()) || 'monthly';
			const priceId = getPriceId(targetKey, interval);
			if (!priceId) {
				throw new Error('No price available for that plan');
			}
			await startCheckout(priceId);
		} catch (err) {
			Toast.error(err?.response?.data?.message || err.message || 'Failed to start upgrade');
			setBusy(false);
		}
	};

	return (
		<div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
			<div className="absolute inset-0 bg-black/70" onClick={busy ? undefined : onClose} />
			<div className="relative z-10 w-full max-w-[520px] rounded-[18px] bg-white shadow-2xl p-7 max-h-[90vh] overflow-y-auto">
				<button
					type="button"
					aria-label="Close"
					className="absolute right-4 top-4 text-gray-400 hover:text-gray-700"
					onClick={onClose}
					disabled={busy}
				>
					<X className="w-5 h-5" />
				</button>

				<h2 className="text-[22px] font-bold text-dark-blue pr-8 leading-snug">
					{headline}
				</h2>
				<p className="mt-3 text-sm text-gray leading-6">
					{limitPayload?.message ||
						(canRenewEarly
							? `Renew ${planDisplay} now to begin a new billing period and reset your allowances, or upgrade for higher limits.`
							: 'Upgrade now to continue without losing your place.')}
				</p>

				{!showUpgradePicker && (
					<ul className="mt-5 space-y-2">
						{(isDocument
							? [
									'Fresh document allowance for the new billing period',
									'More source capacity on higher plans',
									'Advanced document types',
									'Continue working immediately'
								]
							: BENEFITS
						).map((item) => (
							<li key={item} className="flex items-start gap-2 text-sm text-dark-blue">
								<Check className="w-4 h-4 mt-0.5 text-purple shrink-0" />
								<span>{item}</span>
							</li>
						))}
					</ul>
				)}

				{showUpgradePicker ? (
					<div className="mt-6 space-y-3">
						<p className="text-sm font-medium text-dark-blue">Choose an upgrade</p>
						{upgradeTargets.map((plan) => (
							<button
								key={plan.key}
								type="button"
								disabled={busy}
								onClick={() => handleUpgradePlan(plan.key)}
								className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 hover:border-purple hover:bg-lightPurple/40 transition"
							>
								<div className="font-semibold text-dark-blue">{plan.name}</div>
								<div className="text-xs text-gray mt-1">{plan.description}</div>
							</button>
						))}
						<button
							type="button"
							className="text-sm text-purple font-medium"
							onClick={() => setShowUpgradePicker(false)}
							disabled={busy}
						>
							Back
						</button>
					</div>
				) : (
					<div className="mt-6 flex flex-col gap-3">
						{canRenewEarly && (
							<Button
								variant="default"
								className="w-full h-[48px] rounded-[10px]"
								onClick={handleRenew}
								disabled={busy}
							>
								{busy ? 'Working…' : `Renew ${planDisplay} now`}
							</Button>
						)}
						{canUpgrade && canRenewEarly && (
							<button
								type="button"
								disabled={busy}
								onClick={() => setShowUpgradePicker(true)}
								className="w-full h-[48px] rounded-[10px] border border-purple text-purple font-semibold hover:bg-lightPurple/50 disabled:opacity-60"
							>
								View upgrade options
							</button>
						)}
						{canUpgrade && !canRenewEarly && (
							<Button
								variant="default"
								className="w-full h-[48px] rounded-[10px]"
								onClick={() => setShowUpgradePicker(true)}
								disabled={busy}
							>
								Upgrade now
							</Button>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

export function isLimitReachedError(errorOrData) {
	const data = errorOrData?.response?.data || errorOrData;
	if (!data) return false;
	return (
		data.code === 'DOCUMENT_LIMIT' ||
		data.code === 'SOURCE_LIMIT' ||
		(data.status === 403 &&
			typeof data.message === 'string' &&
			(/document limit|source limit|maximum number of documents|maximum number of sources|billing period/i.test(
				data.message
			)))
	);
}
